// Cancer Screening Recommendation Engine v2.1
// Evidence-based risk assessment using USPSTF, ACS, WHO/IARC, NCI SEER data
// and recent peer-reviewed academic research (2022-2025)
//
// ─── PRIMARY GUIDELINE SOURCES ───
// - USPSTF A/B Recommendations (2024)
// - ACS Cancer Screening Guidelines (2025)
// - NCI SEER Cancer Statistics (2017-2021)
// - WCRF/AICR Third Expert Report (2018) + Continuous Update Project
//
// ─── ACADEMIC RESEARCH CITATIONS (2022-2025) ───
// [1] Jun S, et al. "Cancer risk based on alcohol consumption levels: a
//     comprehensive systematic review and meta-analysis." Epidemiol Health.
//     2023;45:e2023092. PMID: 37905315. (139 cohort studies, dose-response
//     alcohol-cancer risk; light drinking: RR 1.02 overall but significant for
//     esophageal, colorectal, breast; heavy ≥50g/day: RR elevated for nearly
//     all cancer types; no safe level of consumption)
//
// [2] Chen J, et al. "Body Mass Index and Cancer Risk: An Umbrella Review of
//     Meta-Analyses of Observational Studies." Nutr Cancer. 2023;75(4):1051-1064.
//     PMID: 37139871. (Each 5 kg/m² BMI increase: 1.01-1.13x risk for brain
//     tumors, myeloma, bladder, pancreatic, breast, NHL; obesity associated with
//     13+ cancer types including cervical, kidney, endometrial, colorectal, liver,
//     thyroid; 1 kg/m² increase = 6% kidney, 4% gallbladder cancer risk increase)
//
// [3] Fontvieille E, et al. "Body mass index and cancer risk among adults with
//     and without cardiometabolic diseases: evidence from the EPIC and UK Biobank
//     prospective cohort studies." BMC Med. 2023;21(1):418. PMID: 37993940.
//     (577,343 adults; BMI per ~5 kg/m² → HR 1.11 for obesity-related cancers;
//     additive interaction between obesity+CVD: RERI 0.28; obesity increases
//     cancer risk irrespective of cardiometabolic disease status)
//
// [4] Hua H, et al. "Risk factors for early-onset colorectal cancer: systematic
//     review and meta-analysis." Frontiers in Oncology. 2023;13:1132306.
//     (Male sex, Caucasian, family history, IBD, obesity/overweight identified
//     as independent risk factors for early-onset CRC)
//
// [5] Hermelink R, et al. "Sedentary behavior and cancer—an umbrella review and
//     meta-analysis." Eur J Epidemiol. 2022;37(5):447-460. PMID: 35612669.
//     (High sedentary behavior associated with increased endometrial (+36%),
//     colon (+12%), and lung cancer risk)
//
// [6] Zhang P, et al. "Association of smoking and polygenic risk with the
//     incidence of lung cancer: a prospective cohort study." Br J Cancer.
//     2022;126(11):1637-1646. PMID: 35194190. (345,794 UK Biobank participants;
//     high genetic risk + heavy smoking → HR 6.19 vs never-smokers with low
//     genetic risk; smoking alone: HR 4.27 for heavy smokers)
//
// [7] Zhan ZQ, et al. "Metabolic syndrome, its components, and gastrointestinal
//     cancer risk: a meta-analysis of 31 prospective cohorts and Mendelian
//     randomization study." J Gastroenterol Hepatol. 2024;39(4):630-641.
//     PMID: 38230882. (MetS increases GI cancer risk; obesity component is
//     independently associated with colorectal, gastric, and liver cancer)
//
// [8] Ng CS, et al. "ALDH2 polymorphism, alcohol intake and the attributable
//     burden of cancer in East Asia." Ann Epidemiol. 2023;85:113-120.e20.
//     PMID: 37268241. (ALDH2-deficient individuals in East Asian populations
//     have significantly higher alcohol-related cancer risk; dose-response
//     curves for liver, esophageal cancers by genotype)
//
// [9] Wang C, et al. "Risk-Adapted Starting Age of Personalized Lung Cancer
//     Screening." Chest. 2024;165(6):1538-1554. PMID: 38253312. (1,031,911
//     participants in China; 7 risk factors identified including pack-years,
//     secondhand smoke, prior lung disease, family history; supports
//     risk-adapted personalized screening ages)
//
// [10] Bethea TN, et al. "A prospective cohort study of physical activity in
//      relation to lung cancer incidence among Black women." Cancer Epidemiol.
//      2022;78:102146. PMID: 35325667. (Black women have higher lung cancer
//      incidence despite lower smoking prevalence — supports ethnicity-specific
//      risk adjustment)

import { t as _t } from "./cancerTranslations";

// ─── CLINICAL DATA ───────────────────────────────────────────────────────────

const BASELINE_LIFETIME_RISK = {
  // NCI SEER lifetime risk (%) - approximate, US population
  lung:       { male: 6.3, female: 5.8 },
  breast:     { female: 12.9 }, // ~1 in 8 women
  colorectal: { male: 4.3, female: 4.0 },
  prostate:   { male: 12.6 }, // ~1 in 8 men
  cervical:   { female: 0.6 },
  ovarian:    { female: 1.2 },
  uterine:    { female: 3.1 },
  liver:      { male: 1.1, female: 0.5 },
  thyroid:    { male: 0.7, female: 2.1 },
  skin:       { male: 3.3, female: 2.3 }, // melanoma
  gastric:    { male: 1.0, female: 0.5 },
  testicular: { male: 0.4 },
};

// Relative risk multipliers from meta-analyses (approximate)
const RISK_MULTIPLIERS = {
  // Smoking (pack-years thresholds)
  // Zhang et al. 2022 (PMID:35194190): heavy ≥40 pk-yr HR 17.89 (15.31-20.91) UK Biobank
  // Doll & Peto 2004 (PMID:15213107): 50-year British doctors cohort, ~15-25x for heavy long-term smokers
  smoking: {
    lung:       { light: 2.0, moderate: 5.0, heavy: 18.0, veryHeavy: 25.0 },
    colorectal: { any: 1.2 },
    liver:      { any: 1.5 },
    gastric:    { any: 1.6 },
    cervical:   { any: 1.6 },
    breast:     { any: 1.1 },
  },
  // Alcohol (drinks/week thresholds per WCRF/AICR + Jun et al. 2023 meta-analysis [1])
  // Jun et al. found: light drinking (0.01-12.4g/day) → significant for esophageal, colorectal, breast
  // Heavy (≥50g/day) → elevated for nearly all cancer types
  // "No safe level of alcohol consumption associated with cancer risk"
  alcohol: {
    liver:      { light: 1.0, moderate: 1.5, heavy: 2.5, veryHeavy: 5.0 },
    breast:     { light: 1.04, moderate: 1.1, heavy: 1.5, veryHeavy: 1.6 }, // light drinking RR from [1]
    colorectal: { light: 1.05, moderate: 1.1, heavy: 1.4, veryHeavy: 1.5 }, // significant even at light [1]
    gastric:    { moderate: 1.1, heavy: 1.2, veryHeavy: 1.5 },
  },
  // BMI categories (WCRF/AICR 2018 + Chen et al. 2023 umbrella review [2] + Fontvieille et al. 2023 [3])
  // Chen 2023: per 5 kg/m² → 1.01-1.13x for multiple cancers; obesity linked to 13+ cancer types
  // Fontvieille 2023: per ~5 kg/m² → HR 1.11 for obesity-related cancers (577K adults, EPIC+UKB)
  bmi: {
    breast_postmeno: { overweight: 1.11, obese: 1.3, severeObese: 1.5 }, // Fontvieille: HR 1.11/5 kg/m²
    colorectal:      { overweight: 1.1, obese: 1.3, severeObese: 1.5 },  // Hua 2023 [4]: obesity = independent CRC risk
    liver:           { overweight: 1.2, obese: 1.8, severeObese: 2.5 },
    uterine:         { overweight: 1.5, obese: 2.5, severeObese: 4.5 },  // Chen 2023: obesity strongest for endometrial
    ovarian:         { overweight: 1.1, obese: 1.3 },
    gastric:         { overweight: 1.2, obese: 1.4 },                    // Zhan 2024 [7]: MetS/obesity → GI cancers
    thyroid:         { overweight: 1.2, obese: 1.3 },                    // Chen 2023: obesity → thyroid cancer
    cervical:        { overweight: 1.1, obese: 1.2 },                    // Chen 2023: obesity → cervical cancer
  },
  // Family history first-degree relative — all values cited to meta-analyses
  familyHistory: {
    lung:       2.0,  // Ang et al. 2020, 84 studies (PMID:32892102): ~1.5-2.0x varies by region/sex
    breast:     2.5,  // Wang et al. 2023, Asian women (PMID:37400822): OR 2.46 (2.03-2.97)
    colorectal: 1.9,  // Mehraban Far 2019, 46 studies, n=906,981 (PMID:31435179): RR 1.87 (1.68-2.09)
    prostate:   2.5,  // Johns & Houlston 2003, 13 studies (PMID:12780833): RR 2.5 (2.2-2.8)
    cervical:   1.5,  // Hemminki et al. 1999 (PMID:10446440): FRR ~1.5 Swedish Family-Cancer Database
    ovarian:    3.1,  // Stratton et al. 1998, pooled analysis (PMID:9637117): RR 3.1 (2.6-3.7)
    uterine:    2.0,  // Win et al. 2015, 16 studies (PMID:25560109): pooled RR ~1.8-2.0
    liver:      2.0,  // General literature, no single FH-specific MA identified
    thyroid:    2.9,  // Fallah et al. 2013 (PMID:23585692): SIR 2.9 (2.4-3.4)
    skin:       1.7,  // Gandini et al. 2005, 60 studies (PMID:16125929): RR 1.74 (1.41-2.14)
    gastric:    2.5,  // Yaghoobi et al. 2017, 32 studies, n=80,690 (PMID:28428723): RR 2.35; FDR 2.71
    testicular: 4.0,  // Hemminki & Li 2004 (PMID:15208620): SIR 4x (father), 8-9x (brother); Kharazmi 2015 (PMID:25913387): 5 Nordic countries, 21,254 TC pts; Dong & Hemminki 2001 (PMID:11576844). Using 4.0x as conservative average across relationship types
  },
  // Ethnicity-specific incidence rate ratios
  // Lui et al. 2014 (PMID:25030941): SEER 1992-2009 ethnic disparities in gastric cancer
  // Gupta et al. 2019 (PMID:30267713): SEER 2000-2014 gastric cancer by race/ethnicity
  // DeSantis et al. 2019 (PMID:30762872): Cancer statistics for African Americans
  ethnicity: {
    african: {
      prostate:   1.7, // DeSantis 2019: AA men ~70% higher incidence
      breast:     1.0, // similar incidence, higher mortality
      colorectal: 1.2,
      lung:       1.2,
      liver:      1.3,
      gastric:    1.7, // Lui 2014: 12.8 vs 7.4/100K = ~1.73x
    },
    asian: {
      liver:      1.8, // HBV prevalence
      gastric:    2.0, // Lui 2014: 15.6 vs 7.4/100K = ~2.1x; Gupta 2019 confirms
      thyroid:    1.3,
      lung:       0.7,
      breast:     0.7,
      colorectal: 0.8,
      prostate:   0.5,
    },
    hispanic: {
      liver:      1.8,
      gastric:    1.7, // Lui 2014: 12.9 vs 7.4/100K = ~1.74x (was 1.5, corrected)
      cervical:   1.3,
      prostate:   0.9,
      breast:     0.8,
    },
    caucasian: {
      skin:       3.0, // melanoma
      breast:     1.0,
      prostate:   1.0,
    },
    middle_eastern: {
      gastric:    1.3,
      liver:      1.2,
    },
  },
  // Secondhand smoke
  // Possenti et al. 2024, 97 studies (PMID:39537242): RR 1.24 (1.16-1.32) overall, 1.38 workplace
  // Kim et al. 2018, 40 studies (PMID:30208628): OR 1.245 for lung, 1.235 for breast
  secondhandSmoke: {
    lung: 1.3, // Between overall (1.24) and workplace (1.38) estimates
  },
  // Radiation exposure
  radiation: {
    thyroid: 3.0,
    lung:    1.5,
    breast:  1.5,
    gastric: 1.3,
  },
  // Smoking + Alcohol interaction (multiplicative for certain cancers)
  // Bui et al. 2024 (PMID:38533737): Korean NHIS nested case-control, 15,352 liver cancer patients,
  //   multiplicative aOR 1.28 (1.11–1.49) for smoke×alcohol in men; additive SI 2.75 (1.85–3.66)
  // Campbell et al. 2016 (PMID:27742674): Liver Cancer Pooling Project, 14 US cohorts, null multiplicative interaction (p≥0.05)
  // Consensus: 1.3x is conservative middle ground between Korean mult aOR 1.28 and null US finding
  smokingAlcoholInteraction: {
    liver:   1.3, // Bui 2024 (PMID:38533737): mult aOR 1.28; Campbell 2016 (PMID:27742674): null in US cohorts
    gastric: 1.0, // Holmes 2021 (PMID:34780551): SI 0.88 for GIM (sub-additive, no synergy); risk driven by smoking alone
  },
  // Chronic disease associations
  chronicDisease: {
    lung_lung:          2.0,
    liver_liver:        3.0,
    liver_hepatitis:    5.0,  // Wang et al. 2025 umbrella review (PMID:39834076): HBV 12.5x, HCV 11.2x — our 5.0x is conservative
    liver_cirrhosis:    8.0,  // Huang et al. 2023 (PMID:35940513): cirrhosis → HCC 5yr cumulative 3%, 10yr 9%
    gastrointestinal_gastric: 2.0,
    gastrointestinal_colorectal: 1.5,
    thyroid_thyroid:     2.5,
    breast_breast:       2.0,
    cervical_cervical:   2.0,
    cervical_hpv:        5.0,
    uterine_uterine:     2.0,
    uterine_endometriosis: 1.8,
    ovarian_endometriosis: 1.5,
    ovarian_pcos:        1.3,
    prostate_prostate:   2.0,
    testicular_testicular: 2.0,
    diabetes_colorectal: 1.3,
    diabetes_liver:      2.0,
    diabetes_breast:     1.2,
    cardiovascular_colorectal: 1.1,
  },
  // Unhealthy diet
  diet: {
    colorectal: 1.3, // processed meat, high sodium
    gastric:    1.5, // high sodium, preserved foods
    liver:      1.2,
  },
};

// ─── PRIMARY vs SECONDARY SCREENING CLASSIFICATION ─────────────────────────
// "Primary" screenings: officially recommended by government guidelines with
// strong evidence (USPSTF Grade A/B, Singapore Healthier SG, Australia NBCSP).
// These are population-level recommendations for average-risk individuals.
//
// "Secondary" screenings: personalized based on individual risk factors
// (family history, lifestyle, ethnicity, chronic conditions). These may lack
// strong population-level recommendations but are clinically relevant for
// high-risk individuals.
//
// Sources researched (Feb 2026):
// - Singapore Healthier SG / Screen for Life: cervical, breast, colorectal
// - Australia: cervical (NCSP), breast (BreastScreen, 40-74), bowel (NBCSP, 50-74), lung (pilot)
// - USPSTF A/B: cervical (A, 21-65), breast (B, 40-74), colorectal (A, 45-75; B, 45-49), lung (B, 50-80 w/ 20 pack-yr)
//
// Primary cancer screenings (government-backed):
//   - Cervical cancer (Pap/HPV) — USPSTF A, SG, AU
//   - Breast cancer (mammography) — USPSTF B, SG, AU
//   - Colorectal cancer (FIT/colonoscopy) — USPSTF A, SG, AU
//   - Lung cancer (LDCT for high-risk smokers) — USPSTF B
//
// Secondary cancer screenings (personalized/research-based):
//   - Prostate, liver, thyroid, ovarian, uterine, skin, gastric, testicular

const PRIMARY_SCREENING_CANCERS = new Set([
  "cervical", "breast", "colorectal", "lung"
]);

// ─── STRICT GOVERNMENT GUIDELINE ELIGIBILITY ────────────────────────────────
// Only users who meet these criteria TODAY see a screening in "National Guidelines".
// Stricter overlap of AU, SG, and USPSTF thresholds.

const GUIDELINE_SOURCES = {
  cervical: "AU NCSP; USPSTF 2018 Grade A",
  breast: "AU BreastScreen; USPSTF 2024 Grade B",
  colorectal: "AU NBCSP; SG Screen for Life; USPSTF 2021 Grade A",
  lung: "USPSTF 2021 Grade B",
};

function meetsGuidelineEligibility(cancerKey, age, sex, surveyData) {
  const isFemale = sex === "female";
  const filteredFamily = (Array.isArray(surveyData.familyCancerHistory) ? surveyData.familyCancerHistory : [])
    .filter(fh => fh.toLowerCase() !== "none");
  const hasFamilyHistory = (keyword) =>
    filteredFamily.some(fh => fh.toLowerCase().includes(keyword));

  switch (cancerKey) {
    case "cervical":
      // Age 25-65, female — strict overlap of AU NCSP (25-74) and USPSTF (21-65)
      return isFemale && age >= 25 && age <= 65;

    case "breast": {
      if (!isFemale) return false;
      // Average risk: age 50-74 (AU BreastScreen funded range)
      if (age >= 50 && age <= 74) return true;
      // High risk: age 30+ with first-degree family history or known BRCA
      const hasBreastFH = hasFamilyHistory("breast");
      const hasBRCA = hasFamilyHistory("brca") ||
        (Array.isArray(surveyData.chronicDiseases) && surveyData.chronicDiseases.some(
          cd => cd.toLowerCase().includes("brca")
        ));
      if (age >= 30 && (hasBreastFH || hasBRCA)) return true;
      return false;
    }

    case "colorectal": {
      // Average risk: age 50-74 (AU NBCSP / SG Screen for Life)
      if (age >= 50 && age <= 74) return true;
      // High risk: age 40+ with family history of colorectal cancer
      const hasCRCFH = hasFamilyHistory("colon") || hasFamilyHistory("bowel") || hasFamilyHistory("colorectal");
      if (age >= 40 && hasCRCFH) return true;
      return false;
    }

    case "lung": {
      // Age 50-80, ≥20 pack-years, currently smokes or quit within 15 years
      if (age < 50 || age > 80) return false;
      const packYears = calculatePackYears(
        surveyData.smokingHistory || 0,
        surveyData.cigarettesPerDay || 0
      );
      if (packYears < 20) return false;
      // Currently smokes (cigarettesPerDay > 0) or former smoker (packYears > 0 but cigarettesPerDay == 0)
      // We can't perfectly determine "quit within 15 years" from survey data,
      // so we consider: if they have pack-years and cigarettesPerDay > 0 → current smoker (eligible)
      // if cigarettesPerDay == 0 but packYears >= 20 → former smoker, assume eligible
      // (conservative: include former smokers since we lack quit-date data)
      return true;
    }

    default:
      return false;
  }
}

function getGuidelineNote(cancerKey, age, sex, surveyData, locale = "en") {
  const isFemale = sex === "female";

  switch (cancerKey) {
    case "cervical":
      if (!isFemale) return null;
      if (age < 25) return _t(locale, "guideline.cervical.under25");
      if (age > 65) return _t(locale, "guideline.cervical.over65");
      return null;

    case "breast":
      if (!isFemale) return null;
      if (age < 30) return _t(locale, "guideline.breast.under30");
      if (age < 50) return _t(locale, "guideline.breast.under50");
      if (age > 74) return _t(locale, "guideline.breast.over74");
      return null;

    case "colorectal":
      if (age < 40) return _t(locale, "guideline.colorectal.under40");
      if (age < 50) return _t(locale, "guideline.colorectal.under50");
      if (age > 74) return _t(locale, "guideline.colorectal.over74");
      return null;

    case "lung":
      if (age < 50) return _t(locale, "guideline.lung.under50");
      if (age > 80) return _t(locale, "guideline.lung.over80");
      return _t(locale, "guideline.lung.default");

    default:
      return null;
  }
}

// USPSTF/ACS Screening recommendations — now locale-aware
function getScreeningGuidelines(locale = "en") {
  const cancerKeys = ["lung", "breast", "cervical", "colorectal", "prostate", "liver", "thyroid", "ovarian", "uterine", "skin", "gastric", "testicular"];
  const fields = ["method", "guideline", "who", "interval", "source", "highRisk"];
  const result = {};
  for (const ck of cancerKeys) {
    result[ck] = {};
    for (const f of fields) {
      const key = `screening.${ck}.${f}`;
      const val = _t(locale, key);
      if (val !== key) result[ck][f] = val;
    }
  }
  return result;
}

// Keep backward compat: default English
const SCREENING_GUIDELINES = getScreeningGuidelines("en");

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function calculateBMI(weightKg, heightCm) {
  if (!weightKg || !heightCm || weightKg <= 0 || heightCm <= 0) return null;
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

function getBMICategory(bmi) {
  if (!bmi) return "normal";
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  if (bmi < 35) return "obese";
  return "severeObese";
}

function getAlcoholCategory(alcoholConsumption) {
  // Based on NIAAA / WCRF definitions
  // none, under_7, 7_to_14, 14_to_25, 25_to_50, over_50
  switch (alcoholConsumption) {
    case "none": return "none";
    case "under_7": return "light";       // <1/day
    case "7_to_14": return "moderate";    // 1-2/day (NIAAA moderate for men)
    case "14_to_25": return "heavy";      // 2-3.5/day
    case "25_to_50": return "veryHeavy";  // >3.5/day
    case "over_50": return "veryHeavy";
    default: return "none";
  }
}

function calculatePackYears(smokingYears, cigarettesPerDay) {
  // Pack-years = (cigarettes per day / 20) × years smoked
  // Survey now asks for number of cigarettes (not packs), so we divide by 20
  const years = parseFloat(smokingYears) || 0;
  const cigs = parseFloat(cigarettesPerDay) || 0;
  const packsPerDay = cigs / 20; // 1 pack = 20 cigarettes
  return years * packsPerDay;
}

function getSmokingCategory(packYears) {
  if (packYears <= 0) return "none";
  if (packYears < 10) return "light";
  if (packYears < 20) return "moderate";
  if (packYears < 30) return "heavy";
  return "veryHeavy";
}

function getRiskLevel(riskScore, locale = "en") {
  if (riskScore >= 4.0) return { level: "HIGH", color: "#dc3545", cssClass: "high-risk", label: _t(locale, "risk.high") };
  if (riskScore >= 2.5) return { level: "ELEVATED", color: "#fd7e14", cssClass: "elevated-risk", label: _t(locale, "risk.elevated") };
  if (riskScore >= 1.5) return { level: "MODERATE", color: "#ffc107", cssClass: "moderate-risk", label: _t(locale, "risk.moderate") };
  return { level: "LOW", color: "#28a745", cssClass: "low-risk", label: _t(locale, "risk.low") };
}

function formatReasons(reasons) {
  if (reasons.length === 0) return "";
  if (reasons.length === 1) return reasons[0];
  return reasons.slice(0, -1).join(", ") + ", and " + reasons.slice(-1);
}

function getBaselineRisk(cancerKey, sex) {
  const data = BASELINE_LIFETIME_RISK[cancerKey];
  if (!data) return null;
  if (data[sex]) return data[sex];
  if (data.male && sex === "male") return data.male;
  if (data.female && sex === "female") return data.female;
  return data.male || data.female || null;
}

function formatRiskAsRatio(pct) {
  if (!pct || pct <= 0) return null;
  const ratio = Math.round(100 / pct);
  return `1 in ${ratio}`;
}

// ─── MAIN RECOMMENDATION ENGINE ─────────────────────────────────────────────

export function getRecommendations(surveyData, locale = "en") {
  const {
    age = 0,
    biologicalSex = "",
    ethnicity = "",
    familyCancerHistory = [],
    weight = 0,
    height = 0,
    chronicDiseases = [],
    smokingHistory = 0,
    cigarettesPerDay = 0,
    livedWithSmoker = "",
    radiationExposure = "",
    alcoholConsumption = "",
    dietHabits = "",
  } = surveyData;

  const validAge = typeof age === "number" && age > 0 && age <= 120;
  const validSex = typeof biologicalSex === "string" &&
    ["male", "female", "other"].includes(biologicalSex.toLowerCase());

  if (!validAge || !validSex) {
    return { stronglyRecommend: [], recommend: [] };
  }

  const sex = biologicalSex.toLowerCase();
  const isFemale = sex === "female";
  const isMale = sex === "male";
  // Normalize new NIH/CDC ethnicity values to internal risk keys
  const ethnicityMap = {
    "white": "caucasian",
    "black": "african",
    "native_american": "african", // closest risk profile available
    "pacific_islander": "asian",  // closest risk profile available
    "prefer_not_to_say": "",
  };
  const rawEth = (ethnicity || "").toLowerCase();
  const ethLower = ethnicityMap[rawEth] || rawEth;

  // Calculate derived values
  const weightKg = surveyData.weightInKg || weight;
  const heightCm = surveyData.heightInCm || height;
  const bmi = calculateBMI(weightKg, heightCm);
  const bmiCategory = getBMICategory(bmi);
  const packYears = calculatePackYears(smokingHistory, cigarettesPerDay);
  const smokingCat = getSmokingCategory(packYears);
  const alcoholCat = getAlcoholCategory(alcoholConsumption);
  const hasRadiation = radiationExposure === "yes";
  const hasSecondhandSmoke = livedWithSmoker === "yes";
  const unhealthyDiet = dietHabits && dietHabits !== "no" && dietHabits !== "unsure";

  const filteredFamily = (Array.isArray(familyCancerHistory) ? familyCancerHistory : [])
    .filter(fh => fh.toLowerCase() !== "none");
  const filteredChronic = (Array.isArray(chronicDiseases) ? chronicDiseases : [])
    .filter(cd => cd.toLowerCase() !== "none");

  const hasFamilyHistory = (keyword) =>
    filteredFamily.some(fh => fh.toLowerCase().includes(keyword));
  const hasChronic = (keyword) =>
    filteredChronic.some(cd => cd.toLowerCase().includes(keyword));
  const ethnicityMultiplier = (cancerKey) => {
    const ethData = RISK_MULTIPLIERS.ethnicity[ethLower];
    return ethData && ethData[cancerKey] ? ethData[cancerKey] : 1.0;
  };

  // ─── Build assessments for each cancer type ───

  const assessments = [];

  const screeningGuidelines = getScreeningGuidelines(locale);

  function assess(cancerKey, label, applicableSex) {
    if (applicableSex === "female" && !isFemale) return;
    if (applicableSex === "male" && !isMale) return;

    const tl = (key, params) => _t(locale, key, params);
    const localLabel = _t(locale, `cancer.${cancerKey}`) || label;

    let riskScore = 1.0; // baseline
    const riskFactors = [];
    const statistics = [];
    const baselineRisk = getBaselineRisk(cancerKey, sex);
    const screening = screeningGuidelines[cancerKey];

    // Age factor
    const ageRisk = getAgeRiskFactor(cancerKey, age, sex);
    if (ageRisk > 1.0) {
      riskScore *= ageRisk;
      riskFactors.push({
        factor: tl("risk.age.factor", { age }),
        detail: getAgeDetail(cancerKey, age, locale),
        multiplier: ageRisk,
      });
    }

    // Family history
    const familyKeywords = getFamilyKeywords(cancerKey);
    const hasFH = familyKeywords.some(k => hasFamilyHistory(k));
    if (hasFH) {
      const mult = RISK_MULTIPLIERS.familyHistory[cancerKey] || 2.0;
      riskScore *= mult;
      riskFactors.push({
        factor: tl("risk.family.factor", { cancer: localLabel.toLowerCase() }),
        detail: tl("risk.family.detail", { cancer: localLabel.toLowerCase(), mult }),
        multiplier: mult,
      });
    }

    // Ethnicity
    const ethMult = ethnicityMultiplier(cancerKey);
    if (ethMult !== 1.0) {
      riskScore *= ethMult;
      const pct = ethMult > 1 ? `+${Math.round((ethMult - 1) * 100)}` : `${Math.round((ethMult - 1) * 100)}`;
      const extra = ethLower === "asian" ? "; Ng et al. 2023, PMID:37268241" : ethLower === "african" ? "; Bethea et al. 2022, PMID:35325667" : "";
      const detailKey = ethMult > 1.0 ? "risk.ethnicity.detail.higher" : "risk.ethnicity.detail.lower";
      riskFactors.push({
        factor: tl("risk.ethnicity.factor", { ethnicity: ethnicity || "unspecified" }),
        detail: tl(detailKey, { ethnicity: ethnicity || "Your ethnic group", cancer: localLabel.toLowerCase(), pct, extra }),
        multiplier: ethMult,
      });

      // Add age context note for young users with high ethnicity risk (e.g. caucasian + skin)
      if (cancerKey === "skin" && ethLower === "caucasian" && age < 40) {
        riskFactors.push({
          factor: tl("risk.ageContext.factor"),
          detail: tl("risk.ageContext.skinYoungCaucasian"),
          multiplier: null,
        });
      }
    }

    // BMI
    const bmiMult = getBMIMultiplier(cancerKey, bmiCategory, age, isFemale);
    if (bmiMult > 1.0 && bmi) {
      riskScore *= bmiMult;
      const bmiCatName = _t(locale, `bmi.${bmiCategory}`);
      riskFactors.push({
        factor: tl("risk.bmi.factor", { bmi: bmi.toFixed(1), category: bmiCatName }),
        detail: tl("risk.bmi.detail", { bmi: bmi.toFixed(1), category: bmiCatName, pct: Math.round((bmiMult - 1) * 100), cancer: localLabel.toLowerCase() }),
        multiplier: bmiMult,
      });
    }

    // Smoking
    const smokeMult = getSmokingMultiplier(cancerKey, smokingCat, packYears);
    if (smokeMult > 1.0 && packYears >= 1) {
      riskScore *= smokeMult;
      riskFactors.push({
        factor: tl("risk.smoking.factor", { packYears: packYears.toFixed(1) }),
        detail: tl("risk.smoking.detail", { packYears: packYears.toFixed(1), cancer: localLabel.toLowerCase(), pct: Math.round((smokeMult - 1) * 100) }),
        multiplier: smokeMult,
      });
    }

    // Secondhand smoke
    if (hasSecondhandSmoke && RISK_MULTIPLIERS.secondhandSmoke[cancerKey]) {
      const shsMult = RISK_MULTIPLIERS.secondhandSmoke[cancerKey];
      riskScore *= shsMult;
      riskFactors.push({
        factor: tl("risk.secondhandSmoke.factor"),
        detail: tl("risk.secondhandSmoke.detail", { cancer: localLabel.toLowerCase(), pct: Math.round((shsMult - 1) * 100) }),
        multiplier: shsMult,
      });
    }

    // Alcohol
    const alcMult = getAlcoholMultiplier(cancerKey, alcoholCat);
    if (alcMult > 1.0) {
      riskScore *= alcMult;
      riskFactors.push({
        factor: `Alcohol consumption (${alcoholCat})`,
        detail: tl("risk.alcohol.detail", { category: alcoholCat.charAt(0).toUpperCase() + alcoholCat.slice(1), cancer: localLabel.toLowerCase(), pct: Math.round((alcMult - 1) * 100) }),
        multiplier: alcMult,
      });
    }

    // Smoking + Alcohol interaction
    if (smokeMult > 1.0 && alcMult > 1.0 && RISK_MULTIPLIERS.smokingAlcoholInteraction[cancerKey]) {
      const interMult = RISK_MULTIPLIERS.smokingAlcoholInteraction[cancerKey];
      riskScore *= interMult;
      riskFactors.push({
        factor: tl("risk.smokingAlcohol.factor"),
        detail: tl("risk.smokingAlcohol.detail", { cancer: localLabel.toLowerCase(), pct: Math.round((interMult - 1) * 100) }),
        multiplier: interMult,
      });
    }

    // Radiation
    if (hasRadiation && RISK_MULTIPLIERS.radiation[cancerKey]) {
      const radMult = RISK_MULTIPLIERS.radiation[cancerKey];
      riskScore *= radMult;
      riskFactors.push({
        factor: tl("risk.radiation.factor"),
        detail: tl("risk.radiation.detail", { cancer: localLabel.toLowerCase(), pct: Math.round((radMult - 1) * 100) }),
        multiplier: radMult,
      });
    }

    // Chronic diseases
    const chronicMult = getChronicDiseaseMultiplier(cancerKey, filteredChronic);
    if (chronicMult.multiplier > 1.0) {
      riskScore *= chronicMult.multiplier;
      riskFactors.push({
        factor: `Chronic condition: ${chronicMult.condition}`,
        detail: chronicMult.detail,
        multiplier: chronicMult.multiplier,
      });
    }

    // Diet
    if (unhealthyDiet && RISK_MULTIPLIERS.diet[cancerKey]) {
      const dietMult = RISK_MULTIPLIERS.diet[cancerKey];
      riskScore *= dietMult;
      riskFactors.push({
        factor: tl("risk.diet.factor"),
        detail: tl("risk.diet.detail", { cancer: localLabel.toLowerCase(), pct: Math.round((dietMult - 1) * 100) }),
        multiplier: dietMult,
      });
    }

    // Build statistics
    if (baselineRisk) {
      const adjustedRisk = Math.min(baselineRisk * riskScore, 95);
      const baseRatio = formatRiskAsRatio(baselineRisk);
      const adjRatio = formatRiskAsRatio(adjustedRisk);
      statistics.push({
        label: "Population baseline lifetime risk",
        value: `${baselineRisk}%${baseRatio ? ` (~${baseRatio})` : ""}`,
      });
      statistics.push({
        label: "Your estimated adjusted risk",
        value: `${adjustedRisk.toFixed(1)}%${adjRatio ? ` (~${adjRatio})` : ""}`,
      });
    }
    statistics.push({
      label: "Combined risk multiplier",
      value: `${riskScore.toFixed(1)}x baseline`,
    });

    const { level, color, cssClass } = getRiskLevel(riskScore);

    // Only include if there are meaningful risk factors or age-appropriate screening
    const minScreeningAge = getMinScreeningAge(cancerKey, sex, riskFactors.length > 0);
    if (age < minScreeningAge && riskFactors.length === 0) return;

    // Determine tier: primary only if meets strict guideline eligibility
    let tier = "secondary";
    let guidelineSource = undefined;
    let guidelineNote = undefined;

    if (PRIMARY_SCREENING_CANCERS.has(cancerKey)) {
      if (meetsGuidelineEligibility(cancerKey, age, sex, surveyData)) {
        tier = "primary";
        guidelineSource = GUIDELINE_SOURCES[cancerKey];
      } else {
        // Still secondary, but add a note about when guidelines apply
        guidelineNote = getGuidelineNote(cancerKey, age, sex, surveyData);
      }
    }

    assessments.push({
      cancer: label,
      cancerKey,
      riskScore,
      riskLevel: level + " RISK",
      riskColor: color,
      cssClass,
      riskFactors,
      statistics,
      screening,
      description: generateDescription(label, riskFactors, level, age),
      tier,
      ...(guidelineSource && { guidelineSource }),
      ...(guidelineNote && { guidelineNote }),
    });
  }

  // Run assessments for all cancer types
  assess("lung", "Lung Cancer", "all");
  assess("breast", "Breast Cancer", "female");
  assess("cervical", "Cervical Cancer", "female");
  assess("ovarian", "Ovarian Cancer", "female");
  assess("uterine", "Uterine Cancer", "female");
  assess("colorectal", "Bowel Cancer", "all");
  assess("liver", "Liver Cancer", "all");
  assess("prostate", "Prostate Cancer", "male");
  assess("testicular", "Testicular Cancer", "male");
  assess("thyroid", "Thyroid Cancer", "all");
  assess("gastric", "Gastrointestinal Cancer", "all");
  assess("skin", "Skin Cancer", "all");

  // Sort by risk score descending
  assessments.sort((a, b) => b.riskScore - a.riskScore);

  // Split into three tiers
  // tier="primary" items (gov-recommended) are promoted to at least "recommend"
  // regardless of risk score, so they always appear in results
  const stronglyRecommend = assessments.filter(a => a.riskScore >= 2.5);
  const recommend = assessments.filter(a => 
    (a.riskScore > 1.2 && a.riskScore < 2.5) || 
    (a.tier === "primary" && a.riskScore < 2.5)
  );
  const belowAverage = assessments.filter(a => a.riskScore <= 1.2 && a.tier !== "primary");

  return { stronglyRecommend, recommend, belowAverage };
}

// ─── HELPER FUNCTIONS ────────────────────────────────────────────────────────

function getAgeRiskFactor(cancerKey, age, sex) {
  // Age-specific risk increases (approximate, based on SEER age-specific incidence)
  const ageFactors = {
    lung:       [{ min: 50, mult: 1.3 }, { min: 60, mult: 2.0 }, { min: 70, mult: 3.0 }],
    breast:     [{ min: 40, mult: 1.2 }, { min: 50, mult: 1.5 }, { min: 60, mult: 2.0 }],
    colorectal: [{ min: 45, mult: 1.2 }, { min: 50, mult: 1.5 }, { min: 60, mult: 2.0 }],
    prostate:   [{ min: 50, mult: 1.3 }, { min: 60, mult: 2.0 }, { min: 70, mult: 3.0 }],
    cervical:   [{ min: 30, mult: 1.1 }, { min: 40, mult: 1.2 }],
    ovarian:    [{ min: 40, mult: 1.2 }, { min: 50, mult: 1.5 }, { min: 60, mult: 2.0 }],
    uterine:    [{ min: 45, mult: 1.3 }, { min: 55, mult: 1.8 }],
    liver:      [{ min: 40, mult: 1.2 }, { min: 55, mult: 1.8 }, { min: 65, mult: 2.5 }],
    thyroid:    [{ min: 30, mult: 1.1 }, { min: 50, mult: 1.2 }],
    skin:       [{ min: 40, mult: 1.2 }, { min: 60, mult: 1.5 }],
    gastric:    [{ min: 40, mult: 1.2 }, { min: 55, mult: 1.8 }, { min: 65, mult: 2.5 }],
    testicular: [], // peak is 15-35, not age-increasing
  };
  const factors = ageFactors[cancerKey] || [];
  let mult = 1.0;
  for (const f of factors) {
    if (age >= f.min) mult = f.mult;
  }
  return mult;
}

function getAgeDetail(cancerKey, age, locale = "en") {
  const key = `age.detail.${cancerKey}`;
  const val = _t(locale, key);
  if (val !== key) return val;
  return _t(locale, "age.detail.default", { age });
}

function getFamilyKeywords(cancerKey) {
  const map = {
    lung: ["lung"],
    breast: ["breast"],
    cervical: ["cervical"],
    ovarian: ["ovarian"],
    uterine: ["uterine", "endometrial"],
    colorectal: ["colon", "bowel", "colorectal"],
    liver: ["liver", "hepatitis"],
    prostate: ["prostate"],
    testicular: ["testicular"],
    thyroid: ["thyroid"],
    gastric: ["gastrointestinal", "gastric", "stomach"],
    skin: ["skin", "melanoma"],
  };
  return map[cancerKey] || [];
}

function getBMIMultiplier(cancerKey, bmiCategory, age, isFemale) {
  if (bmiCategory === "normal" || bmiCategory === "underweight") return 1.0;

  // Breast cancer BMI risk is mainly postmenopausal (Chen et al. 2023 [2])
  let key = cancerKey;
  if (cancerKey === "breast" && isFemale && age >= 50) key = "breast_postmeno";
  else if (cancerKey === "breast") return 1.0; // premenopausal: not clearly increased

  const data = RISK_MULTIPLIERS.bmi[key];
  if (!data) return 1.0;
  return data[bmiCategory] || 1.0;
}

function getSmokingMultiplier(cancerKey, smokingCat, packYears) {
  if (smokingCat === "none") return 1.0;
  const lungData = RISK_MULTIPLIERS.smoking.lung;
  if (cancerKey === "lung") {
    return lungData[smokingCat] || 1.0;
  }
  const otherData = RISK_MULTIPLIERS.smoking[cancerKey];
  if (otherData && otherData.any) return otherData.any;
  return 1.0;
}

function getAlcoholMultiplier(cancerKey, alcoholCat) {
  if (alcoholCat === "none") return 1.0;
  const data = RISK_MULTIPLIERS.alcohol[cancerKey];
  if (!data) return 1.0;
  // Jun et al. 2023 [1]: even light drinking has measurable risk for some cancers
  return data[alcoholCat] || 1.0;
}

function getChronicDiseaseMultiplier(cancerKey, chronicList) {
  let maxMult = 1.0;
  let matchedCondition = "";
  let matchedDetail = "";

  const checks = {
    lung: [["lung", "lung_lung"]],
    liver: [["liver", "liver_liver"], ["hepatitis", "liver_hepatitis"], ["cirrhosis", "liver_cirrhosis"]],
    gastric: [["gastrointestinal", "gastrointestinal_gastric"]],
    colorectal: [
      ["gastrointestinal", "gastrointestinal_colorectal"],
      ["bowel", "gastrointestinal_colorectal"],
      ["colon", "gastrointestinal_colorectal"],
      ["diabetes", "diabetes_colorectal"],
    ],
    thyroid: [["thyroid", "thyroid_thyroid"]],
    breast: [["breast", "breast_breast"], ["diabetes", "diabetes_breast"]],
    cervical: [["cervical", "cervical_cervical"], ["hpv", "cervical_hpv"]],
    uterine: [["uterine", "uterine_uterine"], ["endometriosis", "uterine_endometriosis"], ["endometrial", "uterine_uterine"]],
    ovarian: [["endometriosis", "ovarian_endometriosis"], ["pcos", "ovarian_pcos"], ["ovarian", "ovarian_endometriosis"]],
    prostate: [["prostate", "prostate_prostate"]],
    testicular: [["testicular", "testicular_testicular"]],
  };

  const cancerChecks = checks[cancerKey] || [];
  for (const [keyword, multKey] of cancerChecks) {
    if (chronicList.some(cd => cd.toLowerCase().includes(keyword))) {
      const mult = RISK_MULTIPLIERS.chronicDisease[multKey] || 1.0;
      if (mult > maxMult) {
        maxMult = mult;
        matchedCondition = keyword;
        matchedDetail = `Having chronic ${keyword} conditions increases ${cancerKey} cancer risk by ~${Math.round((mult - 1) * 100)}% (clinical evidence).`;
      }
    }
  }

  return { multiplier: maxMult, condition: matchedCondition, detail: matchedDetail };
}

function getMinScreeningAge(cancerKey, sex, hasRiskFactors) {
  if (hasRiskFactors) return 15; // If risk factors exist, show early
  const ageMap = {
    lung: 50,
    breast: 35,
    cervical: 21,
    ovarian: 35,
    uterine: 30,
    colorectal: 35,
    liver: 35,
    prostate: 45,
    testicular: 15,
    thyroid: 20,
    gastric: 40,
    skin: 25,
  };
  return ageMap[cancerKey] || 30;
}

function generateDescription(label, riskFactors, level, age, locale = "en") {
  const cancer = label.toLowerCase();
  if (riskFactors.length === 0) {
    return _t(locale, "desc.noRisk", { age, cancer });
  }

  const topFactors = riskFactors.slice(0, 3).map(rf => rf.factor.toLowerCase());
  const connector = formatReasons(topFactors);

  if (level === "HIGH") {
    return _t(locale, "desc.high", { factors: connector, cancer });
  }
  if (level === "ELEVATED") {
    return _t(locale, "desc.elevated", { factors: connector, cancer });
  }
  return _t(locale, "desc.default", { factors: connector, cancer });
}

// ─── ACADEMIC REFERENCES (for display on Result Page) ────────────────────────

export const ACADEMIC_REFERENCES = [
  {
    id: 1,
    authors: "Jun S, Park H, Kim UJ, et al.",
    title: "Cancer risk based on alcohol consumption levels: a comprehensive systematic review and meta-analysis",
    journal: "Epidemiol Health",
    year: 2023,
    volume: "45:e2023092",
    pmid: "37905315",
    finding: "139 cohort studies: no safe level of alcohol for cancer risk; even light drinking (0.01-12.4g/day) significantly increases esophageal, colorectal, and breast cancer risk.",
  },
  {
    id: 2,
    authors: "Chen J, Ke K, Liu Z, et al.",
    title: "Body Mass Index and Cancer Risk: An Umbrella Review of Meta-Analyses of Observational Studies",
    journal: "Nutr Cancer",
    year: 2023,
    volume: "75(4):1051-1064",
    pmid: "37139871",
    finding: "Each 5 kg/m² BMI increase → 1.01-1.13x risk for multiple cancers. Obesity linked to 13+ cancer types including cervical, thyroid, colorectal, liver.",
  },
  {
    id: 3,
    authors: "Fontvieille E, Viallon V, Recalde M, et al.",
    title: "Body mass index and cancer risk among adults with and without cardiometabolic diseases: EPIC and UK Biobank",
    journal: "BMC Med",
    year: 2023,
    volume: "21(1):418",
    pmid: "37993940",
    finding: "577,343 adults: per ~5 kg/m², HR 1.11 for obesity-related cancers. Additive interaction between obesity and cardiovascular disease (RERI 0.28).",
  },
  {
    id: 4,
    authors: "Hua H, Jiang Q, Sun P, Xu X.",
    title: "Risk factors for early-onset colorectal cancer: systematic review and meta-analysis",
    journal: "Front Oncol",
    year: 2023,
    volume: "13:1132306",
    pmid: null,
    finding: "Obesity, male sex, Caucasian ethnicity, family history, and IBD are independent risk factors for early-onset CRC.",
  },
  {
    id: 5,
    authors: "Hermelink R, et al.",
    title: "Sedentary behavior and cancer—an umbrella review and meta-analysis",
    journal: "Eur J Epidemiol",
    year: 2022,
    volume: "37(5):447-460",
    pmid: "35612669",
    finding: "High sedentary behavior increases endometrial cancer risk by 36%, colon cancer by 12%, and lung cancer risk.",
  },
  {
    id: 6,
    authors: "Zhang P, Chen PL, Li ZH, et al.",
    title: "Association of smoking and polygenic risk with the incidence of lung cancer: a prospective cohort study",
    journal: "Br J Cancer",
    year: 2022,
    volume: "126(11):1637-1646",
    pmid: "35194190",
    finding: "345,794 UK Biobank participants: heavy smoking → HR 4.27 for lung cancer; combined high genetic risk + heavy smoking → HR 6.19.",
  },
  {
    id: 7,
    authors: "Zhan ZQ, Chen YZ, Huang ZM, et al.",
    title: "Metabolic syndrome, its components, and gastrointestinal cancer risk: meta-analysis of 31 prospective cohorts",
    journal: "J Gastroenterol Hepatol",
    year: 2024,
    volume: "39(4):630-641",
    pmid: "38230882",
    finding: "Metabolic syndrome increases GI cancer risk; obesity is independently associated with colorectal, gastric, and liver cancer.",
  },
  {
    id: 8,
    authors: "Ng CS, Ong XJ, Au M, et al.",
    title: "ALDH2 polymorphism, alcohol intake and the attributable burden of cancer in East Asia",
    journal: "Ann Epidemiol",
    year: 2023,
    volume: "85:113-120.e20",
    pmid: "37268241",
    finding: "ALDH2-deficient East Asian individuals face significantly higher alcohol-related cancer risk with dose-response curves for liver and esophageal cancers.",
  },
  {
    id: 9,
    authors: "Wang C, Dong X, Tan F, et al.",
    title: "Risk-Adapted Starting Age of Personalized Lung Cancer Screening: A Population-Based, Prospective Cohort Study in China",
    journal: "Chest",
    year: 2024,
    volume: "165(6):1538-1554",
    pmid: "38253312",
    finding: "1,031,911 participants: 7 risk factors identified for personalized lung cancer screening including pack-years, secondhand smoke, family history.",
  },
  {
    id: 10,
    authors: "Bethea TN, Dash C, Adams-Campbell LL.",
    title: "A prospective cohort study of physical activity in relation to lung cancer incidence among Black women",
    journal: "Cancer Epidemiol",
    year: 2022,
    volume: "78:102146",
    pmid: "35325667",
    finding: "Black women have higher lung cancer incidence despite lower smoking prevalence, supporting ethnicity-specific risk adjustments.",
  },
  {
    id: 11,
    authors: "Mehraban Far P, Alshahrani A, Yaghoobi M.",
    title: "Quantitative risk of positive family history in developing colorectal cancer: A meta-analysis",
    journal: "World J Gastroenterol",
    year: 2019,
    volume: "25(30):4278-4291",
    pmid: "31435179",
    finding: "46 studies, n=906,981: FH of CRC in first-degree relatives → RR 1.87 (1.68-2.09).",
  },
  {
    id: 12,
    authors: "Wang H, et al.",
    title: "Family history and breast cancer risk for Asian women: a systematic review and meta-analysis",
    journal: "BMC Med",
    year: 2023,
    volume: "21:237",
    pmid: "37400822",
    finding: "First-degree FH → OR 2.46 (2.03-2.97) for Asian women. Similar to European ancestry risk.",
  },
  {
    id: 13,
    authors: "Ang L, et al.",
    title: "Association between family history of lung cancer and lung cancer risk: a systematic review and meta-analysis",
    journal: "Lung Cancer",
    year: 2020,
    volume: "148:129-137",
    pmid: "32892102",
    finding: "84 studies: familial risk ~1.5-2.0x, higher in Asians, younger individuals, and those with ≥2 affected relatives.",
  },
  {
    id: 14,
    authors: "Johns LE, Houlston RS.",
    title: "A systematic review and meta-analysis of familial prostate cancer risk",
    journal: "BJU Int",
    year: 2003,
    volume: "91(9):789-94",
    pmid: "12780833",
    finding: "First-degree relatives: RR 2.5 (2.2-2.8). Two affected relatives: RR 3.5 (2.6-4.8).",
  },
  {
    id: 15,
    authors: "Yaghoobi M, et al.",
    title: "What is the quantitative risk of gastric cancer in the first-degree relatives of patients? A meta-analysis",
    journal: "World J Gastroenterol",
    year: 2017,
    volume: "23(13):2435-2442",
    pmid: "28428723",
    finding: "32 studies, n=80,690: FH of gastric cancer → RR 2.35 (1.96-2.81); first-degree only: 2.71 (2.08-3.53).",
  },
  {
    id: 16,
    authors: "Stratton JF, et al.",
    title: "A systematic review and meta-analysis of family history and risk of ovarian cancer",
    journal: "Br J Obstet Gynaecol",
    year: 1998,
    volume: "105(5):493-499",
    pmid: "9637117",
    finding: "First-degree relatives: RR 3.1 (2.6-3.7). Sisters: 3.8, daughters: 6.0.",
  },
  {
    id: 17,
    authors: "Hemminki K, Li X.",
    title: "Familial risk in testicular cancer as a clue to a heritable and environmental aetiology",
    journal: "Br J Cancer",
    year: 2004,
    volume: "90(9):1765-1770",
    pmid: "15208620",
    finding: "SIR 4-fold (father affected), 9-fold (brother affected).",
  },
  {
    id: 18,
    authors: "Fallah M, et al.",
    title: "Risk of thyroid cancer in first-degree relatives of patients with non-medullary thyroid cancer",
    journal: "J Med Genet",
    year: 2013,
    volume: "50(10):668-673",
    pmid: "23585692",
    finding: "SIR 2.9 (2.4-3.4) for first-degree relatives. ≥2 relatives: 10% cumulative risk.",
  },
  {
    id: 19,
    authors: "Gandini S, et al.",
    title: "Meta-analysis of risk factors for cutaneous melanoma: III. Family history, actinic damage and phenotypic factors",
    journal: "Eur J Cancer",
    year: 2005,
    volume: "41(14):2040-2059",
    pmid: "16125929",
    finding: "60 studies: family history of melanoma → RR 1.74 (1.41-2.14).",
  },
  {
    id: 20,
    authors: "Possenti I, et al.",
    title: "Association between second-hand smoke exposure and lung cancer risk in never-smokers: a systematic review and meta-analysis",
    journal: "Eur Respir Rev",
    year: 2024,
    volume: "33(174)",
    pmid: "39537242",
    finding: "97 studies: SHS → lung cancer RR 1.24 (1.16-1.32) overall; workplace 1.38 (1.28-1.62).",
  },
  {
    id: 21,
    authors: "Lui FH, et al.",
    title: "Ethnic disparities in gastric cancer incidence and survival in the USA: an updated analysis of 1992-2009 SEER data",
    journal: "Dig Dis Sci",
    year: 2014,
    volume: "59(12):3027-3034",
    pmid: "25030941",
    finding: "Asians: 15.6 vs Whites 7.4/100K (~2.1x); Blacks: 12.8 (~1.7x); Hispanics: 12.9 (~1.7x).",
  },
  {
    id: 22,
    authors: "Wang J, et al.",
    title: "Risk factors for hepatocellular carcinoma: an umbrella review of systematic review and meta-analysis",
    journal: "Ann Med",
    year: 2025,
    volume: "57(1):2455539",
    pmid: "39834076",
    finding: "HBV increases HCC risk 12.5-fold; HCV 11.2-fold. Umbrella review of 175 meta-analyses.",
  },
  {
    id: 23,
    authors: "Burton R, et al.",
    title: "The independent and joint risks of alcohol consumption, smoking, and excess weight on morbidity and mortality",
    journal: "Public Health",
    year: 2024,
    volume: "226:134-144",
    pmid: "38000113",
    finding: "Synergy Index: smoke×alcohol→head/neck 3.78; alcohol×weight→liver 1.55. Joint risks exceed additive effects.",
  },
  {
    id: 24,
    authors: "Doll R, Peto R, Boreham J, Sutherland I.",
    title: "Mortality in relation to smoking: 50 years' observations on male British doctors",
    journal: "BMJ",
    year: 2004,
    volume: "328(7455):1519",
    pmid: "15213107",
    finding: "50-year prospective cohort of 34,439 male British doctors. Heavy long-term smokers: ~15-25x lung cancer mortality vs never-smokers.",
  },
  {
    authors: "Bui TT, et al.",
    title: "Combined effects of smoking and alcohol consumption on the risk of liver cancer according to metabolic syndrome",
    journal: "Int J Cancer 2024;155(4):654-665",
    pmid: "38533737",
    finding: "Korean NHIS nested case-control, 15,352 liver cancer patients. Multiplicative aOR 1.28 (1.11–1.49); additive SI 2.75 (1.85–3.66) for smoke×alcohol in men with MetS.",
  },
  {
    authors: "Campbell PT, et al.",
    title: "Body Mass Index, Waist Circumference, Diabetes, and Risk of Liver Cancer for U.S. Adults",
    journal: "Cancer Res 2016;76(20):6076-6083",
    pmid: "27742674",
    finding: "Liver Cancer Pooling Project: 14 US cohorts, 1.5M individuals, 1,423 HCC cases. Null multiplicative interaction between smoking and alcohol (p≥0.05). Significant alcohol×diabetes interaction (p=0.01).",
  },
  {
    authors: "Sjödahl K, et al.",
    title: "Smoking and alcohol drinking in relation to risk of gastric cancer: a population-based, prospective cohort study",
    journal: "Int J Cancer 2007;120(1):128-132",
    pmid: "17036324",
    finding: "Nord-Trøndelag Norway cohort, ~70,000 members, 1.1M person-years. Combined high smoking (>20 cig/day) + frequent alcohol: HR 4.90 (1.90–12.62) for non-cardia gastric cancer.",
  },
  {
    authors: "Praud D, et al.",
    title: "Cigarette smoking and gastric cancer in the Stomach Cancer Pooling (StoP) Project",
    journal: "Eur J Cancer Prev 2018;27(2):124-133",
    pmid: "27560662",
    finding: "StoP Project: 23 studies, 10,290 gastric cancer cases. Ever smokers OR 1.20 (1.09–1.32); current smokers OR 1.25 (1.11–1.40); >20 cig/day OR 1.32 (1.10–1.58).",
  },
  {
    authors: "Wang XQ, et al.",
    title: "Interactions between CagA and smoking in gastric cancer",
    journal: "World J Gastroenterol 2011;17(28):3330-3334",
    pmid: "21876621",
    finding: "Case-control study (257 cases, 514 controls). CagA+ H.pylori × smoking: synergy factor 2.257 (1.133–4.496, p=0.021). CagA+ smokers: aOR 19.5 (10.3–42.2).",
  },
  {
    authors: "Holmes HM, et al.",
    title: "Alcohol consumption and the risk of gastric intestinal metaplasia in a U.S. Veterans population",
    journal: "PLoS One 2021;16(11):e0260019",
    pmid: "34780551",
    finding: "2,084 participants (403 GIM cases), DeBakey VA. SI 0.88 (0.13–6.06) for smoke×alcohol→GIM: sub-additive, no biological synergy. Risk driven by smoking alone.",
  },
  {
    authors: "Kharazmi E, et al.",
    title: "Cancer Risk in Relatives of Testicular Cancer Patients by Histology Type and Age at Diagnosis",
    journal: "Eur Urol 2015;68(2):283-289",
    pmid: "25913387",
    finding: "Joint study from 5 Nordic countries, 97,402 first-degree relatives of 21,254 TC patients (1955-2010). Brothers: ~8x increased risk; fathers/sons: ~4x. Used for: testicular FH multiplier.",
  },
  {
    authors: "Dong C, Hemminki K.",
    title: "Familial testicular cancer and second primary cancers in testicular cancer patients by histological type",
    journal: "Eur J Cancer 2001;37(15):1878-1885",
    pmid: "11576844",
    finding: "Swedish Family-Cancer Database. Familial SIR for testicular cancer in brothers: 8-fold increase. Seminal familial risk study.",
  },
];

// ─── HEALTH MANUAL GENERATOR ────────────────────────────────────────────────

// ─── PERSONALIZED HEALTH MANUAL GENERATOR v2 ─────────────────────────────────
// Returns structured recommendations with titles, details, evidence, and priority

export function generateHealthManual(surveyData, locale = "en") {
  const {
    age = 0,
    biologicalSex = "",
    ethnicity = "",
    weight = 0,
    height = 0,
    chronicDiseases = [],
    smokingHistory = 0,
    cigarettesPerDay = 0,
    livedWithSmoker = "",
    alcoholConsumption = "",
    dietHabits = "",
    familyCancerHistory = [],
  } = surveyData;

  const sex = (biologicalSex || "").toLowerCase();
  const isFemale = sex === "female";
  // Normalize new NIH/CDC ethnicity values to internal risk keys
  const ethnicityMap2 = {
    "white": "caucasian",
    "black": "african",
    "native_american": "african",
    "pacific_islander": "asian",
    "prefer_not_to_say": "",
  };
  const rawEth2 = (ethnicity || "").toLowerCase();
  const ethLower = ethnicityMap2[rawEth2] || rawEth2;
  const weightKg = surveyData.weightInKg || weight;
  const heightCm = surveyData.heightInCm || height;
  const bmi = calculateBMI(weightKg, heightCm);
  const bmiCategory = getBMICategory(bmi);
  const packYears = calculatePackYears(smokingHistory, cigarettesPerDay);
  const alcoholCat = getAlcoholCategory(alcoholConsumption);
  const filteredChronic = (Array.isArray(chronicDiseases) ? chronicDiseases : [])
    .filter(cd => cd.toLowerCase() !== "none");
  const filteredFamily = (Array.isArray(familyCancerHistory) ? familyCancerHistory : [])
    .filter(fh => fh.toLowerCase() !== "none");
  const unhealthyDiet = dietHabits && dietHabits !== "no" && dietHabits !== "unsure";

  // Each item: { title, detail, evidence, priority: "high"|"medium"|"low", icon }
  const diet = [];
  const lifestyle = [];
  const vitamins = [];
  const exercise = [];
  const prevention = [];
  const screening = [];

  // ─── DIET (personalized by BMI, diet habits, chronic conditions) ───

  if (bmi) {
    if (bmiCategory === "severeObese") {
      diet.push({
        title: _t(locale, "manual.diet.bmi.severe.title", { bmi: bmi.toFixed(1) }),
        detail: _t(locale, "manual.diet.bmi.severe.detail", { bmi: bmi.toFixed(1) }),
        evidence: "Fontvieille et al., BMC Med 2023, PMID:37993940; NCI Obesity & Cancer 2022",
        priority: "high",
        icon: "⚖️",
      });
      diet.push({
        title: _t(locale, "manual.diet.calorie.title"),
        detail: _t(locale, "manual.diet.calorie.detail"),
        evidence: "WCRF/AICR 2018; Dominguez et al., Exp Gerontol 2023, PMID:36792040",
        priority: "high",
        icon: "🍽️",
      });
    } else if (bmiCategory === "obese") {
      diet.push({
        title: _t(locale, "manual.diet.bmi.obese.title", { bmi: bmi.toFixed(1) }),
        detail: _t(locale, "manual.diet.bmi.obese.detail", { bmi: bmi.toFixed(1) }),
        evidence: "Chen et al., Nutr Cancer 2023, PMID:37139871; WCRF/AICR 2018",
        priority: "high",
        icon: "⚖️",
      });
    } else if (bmiCategory === "overweight") {
      diet.push({
        title: _t(locale, "manual.diet.bmi.overweight.title", { bmi: bmi.toFixed(1) }),
        detail: _t(locale, "manual.diet.bmi.overweight.detail"),
        evidence: "WCRF/AICR 2018",
        priority: "medium",
        icon: "⚖️",
      });
    } else if (bmiCategory === "underweight") {
      diet.push({
        title: _t(locale, "manual.diet.bmi.underweight.title", { bmi: bmi.toFixed(1) }),
        detail: _t(locale, "manual.diet.bmi.underweight.detail"),
        evidence: "Chen et al., Nutr Cancer 2023, PMID:37139871",
        priority: "medium",
        icon: "⚖️",
      });
    } else {
      diet.push({
        title: _t(locale, "manual.diet.bmi.healthy.title", { bmi: bmi.toFixed(1) }),
        detail: _t(locale, "manual.diet.bmi.healthy.detail"),
        evidence: "WCRF/AICR 2018",
        priority: "low",
        icon: "✅",
      });
    }
  }

  if (unhealthyDiet) {
    diet.push({
      title: _t(locale, "manual.diet.unhealthy.title"),
      detail: _t(locale, "manual.diet.unhealthy.detail"),
      evidence: "IARC Monograph Vol. 114; WCRF/AICR CUP; Bouvard et al., Lancet Oncol 2015",
      priority: "high",
      icon: "🥩",
    });
    diet.push({
      title: _t(locale, "manual.diet.actionable.title"),
      detail: _t(locale, "manual.diet.actionable.detail"),
      evidence: "ACS Nutrition Guidelines 2024; WCRF/AICR 2018",
      priority: "high",
      icon: "🥬",
    });
  } else {
    diet.push({
      title: _t(locale, "manual.diet.good.title"),
      detail: _t(locale, "manual.diet.good.detail"),
      evidence: "Aune et al., Int J Epidemiol 2017, PMID:28338764 (142 publications)",
      priority: "low",
      icon: "🥗",
    });
  }

  // ─── LIFESTYLE (personalized by smoking, alcohol, secondhand smoke) ───

  if (packYears > 0 && cigarettesPerDay > 0) {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.active_smoker.title", { packYears: packYears.toFixed(1) }),
      detail: _t(locale, packYears >= 5 ? "manual.lifestyle.active_smoker.detail" : "manual.lifestyle.active_smoker.detail_light", { packYears: packYears.toFixed(1), hr: packYears >= 20 ? "4-6x" : packYears >= 5 ? "2-3x" : "1.2-1.5x" }),
      evidence: "Zhang et al., Br J Cancer 2022, PMID:35194190; US Surgeon General 2014",
      priority: "high",
      icon: "🚭",
    });
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.cessation.title"),
      detail: _t(locale, "manual.lifestyle.cessation.detail"),
      evidence: "CDC Clinical Practice Guideline for Treating Tobacco Use 2020; Cahill et al., Cochrane 2016",
      priority: "high",
      icon: "💊",
    });
  } else if (packYears > 0 && cigarettesPerDay <= 0) {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.former_smoker.title", { packYears: packYears.toFixed(1) }),
      detail: _t(locale, "manual.lifestyle.former_smoker.detail"),
      evidence: "US Surgeon General 2020; ACS",
      priority: "medium",
      icon: "🎉",
    });
    if (packYears >= 20) {
      lifestyle.push({
        title: _t(locale, "manual.lifestyle.ldct_eligible.title"),
        detail: _t(locale, "manual.lifestyle.ldct_eligible.detail", { packYears: packYears.toFixed(1) }),
        evidence: "USPSTF 2021 (Grade B); National Lung Screening Trial",
        priority: "high",
        icon: "🔬",
      });
    }
  }

  if (livedWithSmoker === "yes" && packYears <= 0) {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.secondhand.title"),
      detail: _t(locale, "manual.lifestyle.secondhand.detail"),
      evidence: "EPA 1992; US Surgeon General 2006; Wang et al., Chest 2024, PMID:38253312",
      priority: "medium",
      icon: "🏠",
    });
  }

  if (alcoholCat === "veryHeavy" || alcoholCat === "heavy") {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.heavy_alcohol.title"),
      detail: _t(locale, "manual.lifestyle.heavy_alcohol.detail", { category: alcoholCat }) + (ethLower === "asian" ? " " + _t(locale, "manual.lifestyle.heavy_alcohol.detail.asian") : ""),
      evidence: "Jun et al., Epidemiol Health 2023, PMID:37905315; IARC Group 1 carcinogen",
      priority: "high",
      icon: "🍷",
    });
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.alcohol_reduction.title"),
      detail: _t(locale, "manual.lifestyle.alcohol_reduction.detail"),
      evidence: "ACS Alcohol & Cancer Guidelines; NIAAA",
      priority: "high",
      icon: "📝",
    });
  } else if (alcoholCat === "moderate") {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.moderate_alcohol.title"),
      detail: _t(locale, "manual.lifestyle.moderate_alcohol.detail"),
      evidence: "Jun et al., Epidemiol Health 2023, PMID:37905315; ACS Guidelines",
      priority: "medium",
      icon: "🍷",
    });
  } else if (alcoholCat === "light") {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.light_alcohol.title"),
      detail: _t(locale, "manual.lifestyle.light_alcohol.detail"),
      evidence: "Jun et al., Epidemiol Health 2023, PMID:37905315",
      priority: "low",
      icon: "ℹ️",
    });
  }

  if (lifestyle.length === 0) {
    lifestyle.push({
      title: _t(locale, "manual.lifestyle.none.title"),
      detail: _t(locale, "manual.lifestyle.none.detail"),
      evidence: "ACS Cancer Prevention Guidelines 2024",
      priority: "low",
      icon: "✅",
    });
  }

  // ─── EXERCISE (personalized by BMI, age, chronic conditions) ───

  const hasCardio = filteredChronic.some(cd => cd.toLowerCase().includes("cardiovascular"));
  const hasDiabetes = filteredChronic.some(cd => cd.toLowerCase().includes("diabetes"));

  if (bmiCategory === "obese" || bmiCategory === "severeObese") {
    exercise.push({
      title: _t(locale, "manual.exercise.obese.title"),
      detail: _t(locale, "manual.exercise.obese.detail"),
      evidence: "WCRF/AICR 2018; Moore et al., JAMA Intern Med 2016, PMID:27183032 (1.44M participants)",
      priority: "high",
      icon: "🏃",
    });
  } else {
    exercise.push({
      title: _t(locale, "manual.exercise.normal.title"),
      detail: _t(locale, "manual.exercise.normal.detail"),
      evidence: "WCRF/AICR 2018 (convincing evidence); Moore et al., JAMA Intern Med 2016, PMID:27183032",
      priority: "medium",
      icon: "🏃",
    });
  }

  exercise.push({
    title: _t(locale, "manual.exercise.strength.title"),
    detail: _t(locale, "manual.exercise.strength.detail") + (hasCardio ? " " + _t(locale, "manual.exercise.strength.detail.cardio") : ""),
    evidence: "Momma et al., Br J Sports Med 2022, PMID:35228201; WHO 2020",
    priority: "medium",
    icon: "💪",
  });

  exercise.push({
    title: _t(locale, "manual.exercise.sitting.title"),
    detail: _t(locale, "manual.exercise.sitting.detail"),
    evidence: "Hermelink et al., Eur J Epidemiol 2022, PMID:35612669",
    priority: "medium",
    icon: "🪑",
  });

  // ─── VITAMINS & SUPPLEMENTS (personalized by age, sex, chronic conditions) ───

  if (age > 50) {
    vitamins.push({
      title: _t(locale, "manual.vitamins.vitd.title"),
      detail: _t(locale, "manual.vitamins.vitd.detail"),
      evidence: "Manson et al., NEJM 2019;380:33-44 (VITAL trial); IOM 2011",
      priority: "medium",
      icon: "☀️",
    });
    vitamins.push({
      title: _t(locale, "manual.vitamins.calcium.title"),
      detail: _t(locale, "manual.vitamins.calcium.detail"),
      evidence: "IOM 2011; USPSTF 2018",
      priority: "low",
      icon: "🦴",
    });
  }

  if (isFemale) {
    if (age < 50) {
      vitamins.push({
        title: _t(locale, "manual.vitamins.folate.title"),
        detail: _t(locale, "manual.vitamins.folate.detail"),
        evidence: "USPSTF 2023 (Grade A for folic acid); WHO Iron Guidelines",
        priority: "medium",
        icon: "💊",
      });
    }
  }

  vitamins.push({
    title: _t(locale, "manual.vitamins.antioxidant.title"),
    detail: _t(locale, "manual.vitamins.antioxidant.detail"),
    evidence: "Klein et al., JAMA 2011 (SELECT); ATBC Study, NEJM 1994; USPSTF 2022 (Grade I)",
    priority: "high",
    icon: "⚠️",
  });

  // ─── PREVENTION & SCREENING (personalized by age, sex, ethnicity, family history, risk factors) ───

  // Personalized screening timeline
  if (age >= 45 || filteredFamily.some(fh => fh.toLowerCase().includes("colon") || fh.toLowerCase().includes("bowel"))) {
    screening.push({
      title: _t(locale, "manual.screening.colorectal.title"),
      detail: (age >= 45 ? _t(locale, "manual.screening.colorectal.detail.age", { age }) : _t(locale, "manual.screening.colorectal.detail.family")) + _t(locale, "manual.screening.colorectal.detail.options"),
      evidence: "USPSTF 2021 (Grade A, 45-75)",
      priority: "high",
      icon: "🔬",
    });
  }

  if (isFemale && age >= 40) {
    screening.push({
      title: _t(locale, "manual.screening.breast.title"),
      detail: _t(locale, "manual.screening.breast.detail", { age }) + " " + (filteredFamily.some(fh => fh.toLowerCase().includes("breast")) ? _t(locale, "manual.screening.breast.detail.family") : _t(locale, "manual.screening.breast.detail.nofamily")),
      evidence: "USPSTF 2024 (Grade B, biennial 40-74); ACS 2015",
      priority: "high",
      icon: "🩺",
    });
  } else if (isFemale && age >= 21) {
    screening.push({
      title: _t(locale, "manual.screening.cervical.title"),
      detail: _t(locale, "manual.screening.cervical.detail") + (age <= 45 ? " " + _t(locale, "manual.screening.cervical.detail.hpv") : ""),
      evidence: "USPSTF 2018 (Grade A); CDC HPV Vaccination 2024",
      priority: "medium",
      icon: "🔬",
    });
  }

  if (sex === "male" && age >= 40 && (ethLower === "african" || filteredFamily.some(fh => fh.toLowerCase().includes("prostate")))) {
    screening.push({
      title: _t(locale, "manual.screening.prostate.title"),
      detail: (ethLower === "african" ? _t(locale, "manual.screening.prostate.detail.african") : _t(locale, "manual.screening.prostate.detail.family")) + _t(locale, "manual.screening.prostate.detail.suffix"),
      evidence: "USPSTF 2018 (Grade C, 55-69); PCF Black Men Guidelines 2023",
      priority: "high",
      icon: "🩺",
    });
  }

  if (ethLower === "asian" || filteredFamily.some(fh => fh.toLowerCase().includes("liver") || fh.toLowerCase().includes("hepatitis"))) {
    screening.push({
      title: _t(locale, "manual.screening.liver.title"),
      detail: (ethLower === "asian" ? _t(locale, "manual.screening.liver.detail.asian") : "") + _t(locale, "manual.screening.liver.detail"),
      evidence: "USPSTF HCV 2020 (Grade B); AASLD 2023",
      priority: "high",
      icon: "🏥",
    });
  }

  // General prevention based on chronic conditions
  if (hasDiabetes) {
    prevention.push({
      title: _t(locale, "manual.prevention.diabetes.title"),
      detail: _t(locale, "manual.prevention.diabetes.detail"),
      evidence: "Zhan et al., J Gastroenterol Hepatol 2024, PMID:38230882; NCI",
      priority: "high",
      icon: "🩸",
    });
  }

  if (hasCardio) {
    prevention.push({
      title: _t(locale, "manual.prevention.cvd.title"),
      detail: _t(locale, "manual.prevention.cvd.detail"),
      evidence: "Fontvieille et al., BMC Med 2023, PMID:37993940",
      priority: "high",
      icon: "❤️",
    });
  }

  // Sleep — universal recommendation
  prevention.push({
    title: _t(locale, "manual.prevention.sleep.title"),
    detail: _t(locale, "manual.prevention.sleep.detail"),
    evidence: "IARC Monograph Vol. 124 (2019); Rivera et al., Int J Cancer 2020",
    priority: "medium",
    icon: "😴",
  });

  // Skin protection — universal
  prevention.push({
    title: _t(locale, "manual.prevention.uv.title"),
    detail: _t(locale, "manual.prevention.uv.detail") + (ethLower === "caucasian" ? " " + _t(locale, "manual.prevention.uv.detail.caucasian") : ""),
    evidence: "AAD; Green et al., J Clin Oncol 2011 (sunscreen reduces melanoma by 50%)",
    priority: ethLower === "caucasian" ? "high" : "medium",
    icon: "☀️",
  });

  if (screening.length === 0) {
    screening.push({
      title: _t(locale, "manual.screening.age_appropriate.title"),
      detail: _t(locale, "manual.screening.age_appropriate.detail", { age }),
      evidence: "USPSTF A/B Recommendations 2024",
      priority: "medium",
      icon: "📅",
    });
  }

  return { diet, lifestyle, vitamins, exercise, prevention, screening };
}
